import { Container, Row, Col, Card, CardHeader, CardBody, Form, Input, FormGroup, Label, Button, ButtonGroup, FormText, Spinner, TabContent, TabPane, } from "reactstrap";
import React, { useEffect, useState, useCallback, useRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faInfoCircle, faCogs, faAd, faArrowLeft, faSearchPlus, faSearchMinus, faExpandArrowsAlt, faDesktop, faMobileAlt, faPaintBrush, } from "@fortawesome/free-solid-svg-icons";
import footballWidgetService from "../../services/api/football-widget-service.js";
import AdditionalSettings from "./AdditionalSettings";
import AdSettings from "./AdSettings";
import TemplateSettings from "./TemplateSettings";

const CreateFootballWidget = () => {
  const history = useHistory();
  const selectStyles = {
    menu: (base) => ({
      ...base,
      position: "absolute",
      zIndex: 9999,
    }),
  };

  const [formDetails, setFormDetails] = useState({
    widget_name: "",
    widget_slug: "",
    client_name: "",
    client_slug: "",
    template: "",
    layout: "carousel",
    tournaments: [],
    rounds: [],
    matches: [],
    tournament_slug: "",
    round_slug: "",
    match_slugs: [],
    title: "",
    custom_css: "",
    domains: [{ domain: "", isWildCard: false }],
    widget_data: {
      ads_data: {
        ad_codes: [],
        header_script: "",
        body_script: "",
      },
      template_data: {}, 
    },
  });
  const matchStatusOptions = [
    { label: "All Matches", value: "all" },
    { label: "Live", value: "started" },
    { label: "Scheduled", value: "not_started" },
    { label: "Completed", value: "completed" },
  ];
  const [tournaments, setTournaments] = useState([]);
  const [allRounds, setAllRounds] = useState([]);
  const [tournamentRounds, setTournamentRounds] = useState([]);
  const [matches, setMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [publisherOptions, setPublisherOptions] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchSelectionType, setMatchSelectionType] = useState("all");
  const [selectedMatchStatus, setSelectedMatchStatus] = useState(matchStatusOptions.find(opt => opt.value === 'all'));
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState({});
  const [previewHtml, setPreviewHtml] = useState(
    '<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding: 20px;"> <h3>Widget Preview</h3> <p>Your widget preview will appear here once you select a template and data source.</p> </div>'
  );
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [previewScale, setPreviewScale] = useState(1);
  const [previewMode, setPreviewMode] = useState("desktop");
  const previewContainerRef = useRef(null);
  const [noMatchesFound, setNoMatchesFound] = useState(false);
  const [noMatchesForFilter, setNoMatchesForFilter] = useState(false);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const fitPreviewToScreen = useCallback(() => {
    if (previewContainerRef.current) {
      setTimeout(() => {
        if (!previewContainerRef.current) return;
        const containerRect =
          previewContainerRef.current.getBoundingClientRect();

        if (previewMode === "desktop") {
          const virtualWidth = 1320;
          const virtualHeight = 760;
          if (containerRect.width > 0 && containerRect.height > 0) {
            const widthScale = containerRect.width / virtualWidth;
            const heightScale = containerRect.height / virtualHeight;
            const newScale = Math.min(widthScale, heightScale) * 0.95;
            setPreviewScale(newScale);
          }
        } else {
          const mobileViewport =
            previewContainerRef.current.querySelector(".mobile-viewport");
          if (mobileViewport) {
            const virtualWidth = 375 + 20;
            const virtualHeight = 812 + 20;
            if (containerRect.width > 0 && containerRect.height > 0) {
              const widthScale = containerRect.width / virtualWidth;
              const heightScale = containerRect.height / virtualHeight;
              const newZoom = Math.min(widthScale, heightScale) * 0.98;
              mobileViewport.style.zoom = newZoom;
            }
          }
        }
      }, 50);
    }
  }, [previewMode]);

  useEffect(() => {
    fitPreviewToScreen();
    window.addEventListener("resize", fitPreviewToScreen);
    return () => {
      window.removeEventListener("resize", fitPreviewToScreen);
    };
  }, [fitPreviewToScreen]);

  const handleModeChange = (mode) => {
    if (previewContainerRef.current) {
      const mobileViewport =
        previewContainerRef.current.querySelector(".mobile-viewport");
      if (mobileViewport) {
        mobileViewport.style.zoom = 1;
      }
    }
    setPreviewScale(1);
    setPreviewMode(mode);
  };

  const layouts = [
    { label: "Carousel", value: "carousel" },
    { label: "Listing", value: "listing" },
  ];

  const templates = [
    { label: "Tournament Matches / Single Card", value: "tournament" },
    { label: "Full Match Detail", value: "full-match-detail" },
    { label: "Points Table", value: "tournament-points-table" },
    { label: "Recent Tournaments List", value: "recent-tournaments-list" },
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [tournamentsRes, roundsRes, clientsRes, configRes] =
          await Promise.all([
            footballWidgetService.getFootballTournaments(),
            footballWidgetService.getFootballRounds(),
            footballWidgetService.getAllClients(),
            footballWidgetService.getConfigSchema(),
          ]);

        if (tournamentsRes.status) setTournaments(tournamentsRes.data.data || []);
        if (roundsRes.status) setAllRounds(roundsRes.data.data || []);

        if (clientsRes.status && Array.isArray(clientsRes.data)) {
          setPublisherOptions(
            clientsRes.data.map((p) => ({
              label: p.client_name,
              value: p.client_slug,
            }))
          );
        }
        if (configRes.status) setWidgetConfig(configRes.data);
      } catch (err) {
        toast.error("Error fetching initial data!");
      }
    };
    fetchInitialData();
  }, []);

  const fetchMatchesForTournament = useCallback(async (tournamentSlug) => {
    try {
      const matchesRes = await footballWidgetService.getMatchesByTournament(
        tournamentSlug
      );
      if (matchesRes.status) {
        setAllMatches(matchesRes.data.data.data || []);
        setNoMatchesFound(false); 
      } else {
        setAllMatches([]);
      }
    } catch (err) {
      toast.error("Error fetching tournament matches!");
      setAllMatches([]);
    }
  }, []);

  const fetchMatchesForRound = useCallback(async (roundSlug) => {
    try {
      const matchesRes = await footballWidgetService.getMatchesByRound(roundSlug);
      const fetchedMatches = matchesRes.data?.data?.data || [];
      if (matchesRes.status === true) {
        setAllMatches(fetchedMatches);
        setNoMatchesFound(fetchedMatches.length === 0);
      } else {
        setAllMatches([]);
        setNoMatchesFound(true);
      }
    } catch (err) {
      toast.error("Error fetching round matches!");
      setAllMatches([]);
      setNoMatchesFound(true);
    }
  }, []);

  useEffect(() => {
    if (selectedTournament?.value) {
      const tournamentSlug = selectedTournament.value;
      const tournament = tournaments.find(t => t.t_slug === tournamentSlug);

      setTournamentRounds(
        tournament?.rounds
          ? allRounds.filter(r => tournament.rounds.some(tr => tr.key === r.key))
          : []
      );

      fetchMatchesForTournament(tournamentSlug);

      setSelectedRound(null);
      setSelectedMatch(null);
      setMatchSelectionType("all");
      setNoMatchesFound(false);
      setNoMatchesForFilter(false);

      setFormDetails(prev => ({
        ...prev,
        tournament_slug: tournamentSlug,
        rounds: [],
        matches: [],
        round_slug: "",
        match_slug: "",
      }));
    } else {
      setTournamentRounds([]);
      setAllMatches([]);
      setNoMatchesForFilter(false);
      setFormDetails(prev => ({
        ...prev,
        tournaments: [],
        tournament_slug: "",
        rounds: [],
        round_slug: "",
        matches: [],
        match_slug: "",
      }));
    }
  }, [selectedTournament, tournaments, allRounds, fetchMatchesForTournament]);

  useEffect(() => {
    if (selectedRound?.value) {
      const roundSlug = selectedRound.value;
      if (roundSlug) {
        setAllMatches([]); 
        setMatches([]);    
        setNoMatchesFound(false);
        fetchMatchesForRound(roundSlug);
      }
      setFormDetails(prev => ({ ...prev, round_slug: roundSlug }));
    } else if (selectedTournament?.value) {
      const tournamentSlug = selectedTournament.value;
      if (tournamentSlug) {
        fetchMatchesForTournament(tournamentSlug);
      }
      setFormDetails(prev => ({ ...prev, round_slug: "" }));
      setNoMatchesFound(false); 
    }
    setSelectedMatch(null);
    setMatchSelectionType("all");
    setNoMatchesForFilter(false);
  }, [selectedRound, selectedTournament, allRounds, fetchMatchesForRound, fetchMatchesForTournament]);

  useEffect(() => {
    setSelectedMatch(null);
    const filtered = allMatches.filter(
      (match) =>
        !selectedMatchStatus ||
        selectedMatchStatus.value === "all" ||
        match.match.status === selectedMatchStatus.value
    );
    setMatches(filtered);
    if (
      filtered.length === 0 &&
      allMatches.length > 0 &&
      selectedMatchStatus &&
      selectedMatchStatus.value !== "all"
    ) {
      setNoMatchesForFilter(true);
    } else {
      setNoMatchesForFilter(false);
    }
  }, [selectedMatchStatus, allMatches]);

  useEffect(() => {
    let finalMatchSlugs = [];

    if (
      formDetails.template === "tournament" ||
      formDetails.template === "full-match-detail"
    ) {
      if (matchSelectionType === "all") {
        finalMatchSlugs = matches.map((m) => m.match.m_slug).filter(Boolean);
      } else if (matchSelectionType === "single" && selectedMatch?.value) {
        finalMatchSlugs = [selectedMatch.value];
      }
    }

    if (formDetails.template === "full-match-detail" && selectedMatch?.value) {
      finalMatchSlugs = [selectedMatch.value];
    }

    setFormDetails((prev) => ({
      ...prev,
      match_slugs: finalMatchSlugs,
    }));
  }, [selectedMatch, matches, matchSelectionType, formDetails.template]);

  useEffect(() => {
    if (selectedRound && noMatchesFound) {
      setPreviewHtml(
        '<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;"><h3>No Matches Found</h3><p>This round has no matches. Please select a different round.</p></div>'
      );
      return;
    }
    if (
      selectedRound &&
      noMatchesForFilter &&
      formDetails.template === "tournament" &&
      matchSelectionType === "all"
    ) {
      setPreviewHtml(
        `<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;">
           <h3>No ${selectedMatchStatus?.label || ''} Matches</h3>
           <p>There are no ${selectedMatchStatus?.label?.toLowerCase() || ''} matches in this round.</p>
         </div>`
      );
      return;
    }

    const handler = setTimeout(async () => {
      const payload = { ...formDetails };

      let finalMatchSlugs = [];
      if (payload.template === "tournament" || payload.template === "full-match-detail") {
        if (matchSelectionType === "all") {
          finalMatchSlugs = matches.map((m) => m.match.m_slug).filter(Boolean);
        } else if (matchSelectionType === "single" && selectedMatch?.value) {
          finalMatchSlugs = [selectedMatch.value];
        }
      }
      if (payload.template === "full-match-detail" && selectedMatch?.value) {
        finalMatchSlugs = [selectedMatch.value];
      }
      payload.match_slugs = finalMatchSlugs;

      if (
        payload.template === "tournament" &&
        matchSelectionType === "single" &&
        selectedMatch?.value
      ) {
        payload.template = "single-match";
        delete payload.round_slug;
      }

      if (!payload.template) {
        setPreviewHtml(
          '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-family:sans-serif;color:#666;">Select a template to see preview</div>'
        );
        return;
      }

      const requiresTournamentSlug = [
        "tournament", "single-match", "full-match-detail", "tournament-points-table",
      ];

      const requiresSingleMatch =
        payload.template === "full-match-detail" ||
        (payload.template === "tournament" && matchSelectionType === "single");

      if (requiresSingleMatch && payload.match_slugs.length === 0) {
        if (noMatchesFound) {
          setPreviewHtml(
            '<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;"><h3>No Matches Found</h3><p>This round has no matches. Please select a different round.</p></div>'
          );
        } else if (noMatchesForFilter) {
          setPreviewHtml(
            `<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;">
               <h3>No ${selectedMatchStatus?.label || ''} Matches</h3>
               <p>There are no ${selectedMatchStatus?.label?.toLowerCase() || ''} matches available to select.</p>
             </div>`
          );
        } else {
          setPreviewHtml(
            '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-family:sans-serif;color:#666;">Select a match to see preview</div>'
          );
        }
        return; 
      }

      if (
        requiresTournamentSlug.includes(payload.template) &&
        !payload.tournament_slug
      ) {
        setPreviewHtml(
          '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-family:sans-serif;color:#666;">Select a tournament to see preview</div>'
        );
        return;
      }

      setIsFetchingPreview(true);
      try {
        const res = await footballWidgetService.getWidgetPreview(payload);
        if (res.status) {
          setPreviewHtml(res.data);
        } else {
          setPreviewHtml(
            `<p style="color:red;font-family:sans-serif;padding:1rem;">Error: ${res.msg || 'Could not load preview.'}</p>`
          );
        }
      } catch (error) {
        setPreviewHtml(
          '<p style="color:red;font-family:sans-serif;padding:1rem;">Could not fetch preview.</p>'
        );
      }
      setIsFetchingPreview(false);
    }, 1000);

    return () => clearTimeout(handler);
  }, [formDetails, matchSelectionType, selectedMatch, matches, noMatchesFound, selectedRound]);

  const generateSlug = (text) =>
    text
      ? text
        .toLowerCase()
        .replace(/ /g, "-")
        .replace(/[^\w-]+/g, "")
      : "";
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormDetails((prev) => ({
      ...prev,
      [name]: value,
      widget_slug:
        name === "widget_name" ? generateSlug(value) : prev.widget_slug,
    }));
  };
  const handlePublisherChange = (newValue) => {
    setSelectedPublisher(newValue);
    setFormDetails({
      ...formDetails,
      client_name: newValue ? newValue.label : "",
      client_slug: newValue ? newValue.value : "",
    });
  };
  const handleCreatePublisher = (inputValue) => {
    const newPublisher = { label: inputValue, value: generateSlug(inputValue) };
    setPublisherOptions([...publisherOptions, newPublisher]);
    setSelectedPublisher(newPublisher);
    setFormDetails({
      ...formDetails,
      client_name: newPublisher.label,
      client_slug: newPublisher.value,
    });
  };
  const isValidDomain = (domain) =>
    /^(\*\.)?([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/.test(
      domain
    );
  const handleDomainChange = (index, value) => {
    const updatedDomains = [...formDetails.domains];
    updatedDomains[index] = {
      domain: value,
      isWildCard: value.startsWith("*."),
    };
    setFormDetails({ ...formDetails, domains: updatedDomains });
  };
  const handleAddDomain = () => {
    const lastDomain = formDetails.domains[formDetails.domains.length - 1];
    if (
      formDetails.domains.length > 0 &&
      (!lastDomain || !lastDomain.domain || !isValidDomain(lastDomain.domain))
    ) {
      return toast.error(
        "Please enter a valid domain before adding a new one."
      );
    }
    setFormDetails({
      ...formDetails,
      domains: [...formDetails.domains, { domain: "", isWildCard: false }],
    });
  };
  const handleRemoveDomain = (index) => {
    const updatedDomains = formDetails.domains.filter((_, i) => i !== index);
    setFormDetails({ ...formDetails, domains: updatedDomains });
  };

  const handleLayoutChange = (selectedOption) => {
    const newLayout = selectedOption ? selectedOption.value : "";
    setFormDetails((prev) => ({
      ...prev,
      layout: newLayout,
    }));
  };

  const handleCreate = async () => {
    if (!formDetails.widget_name.trim()) {
      toast.error("Widget Name is a required field.");
      return;
    }
    if (!formDetails.client_name.trim()) {
      toast.error("Publisher is a required field.");
      return;
    }
    if (!formDetails.template) {
      toast.error("Template is a required field.");
      return;
    }
    const requiresTournament =
      formDetails.template !== "recent-tournaments-list";
    if (requiresTournament && !formDetails.tournament_slug) {
      toast.error("Tournament is a required field for this template.");
      return;
    }
    if (formDetails.template === 'tournament' && noMatchesFound) {
      toast.error("Cannot create widget. The selected round has no matches. Please choose a different one.");
      return;
    }


    const payload = { ...formDetails };
    if (
      payload.template === "tournament" &&
      matchSelectionType === "single" &&
      selectedMatch?.value
    ) {
      payload.template = "single-match";
    }
    const filteredDomains = payload.domains.filter(
      (d) => d.domain && d.domain.trim() !== ""
    );
    payload.widget_data = {
      ...formDetails.widget_data,
      domains: filteredDomains,
      custom_css: payload.custom_css,
      // template_data: {},
    };
    delete payload.domains;
    delete payload.custom_css;
    try {
      const result = await footballWidgetService.createFootballWidget(payload);
      if (result.status) {
        toast.success("Widget Created Successfully!");
        setTimeout(() => history.replace(`/dashboard/football-widget`), 1500);
      } else {
        toast.error(result.msg || "Failed to create widget.");
      }
    } catch (error) {
      toast.error("An error occurred while creating the widget.");
    }
  };

  const showDataSource = formDetails.template && formDetails.template !== "recent-tournaments-list";
  const isCreateDisabled = isFetchingPreview || (formDetails.template === 'tournament' && noMatchesFound);

  return (
    <div className="content">
      <style>{`
        .modern-tab { padding: 12px 20px; cursor: pointer; background: none; border: none; color: #6c757d; font-weight: 600; font-size: 0.9rem; transition: color 0.2s ease-in-out, border-bottom 0.2s ease-in-out; border-bottom: 3px solid transparent; }
        .truncate-placeholder::placeholder {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
        .modern-tab.active { color: #0d6efd; border-bottom: 3px solid #0d6efd; }
        .modern-tab:disabled { color: #adb5bd; cursor: not-allowed; }
        .form-label { font-weight: 600; font-size: 0.85rem; color: #555; }
        .card { transition: box-shadow 200ms ease !important; }
        .preview-wrapper { width: 100%; background-color: #f8f9fa; border-radius: 0.5rem; padding: 1rem; position: sticky; top: 20px; }
        
        .preview-iframe-container { width: 100%; min-height: 500px; height: calc(100vh - 240px); border: 1px solid #dee2e6; border-radius: 0.25rem; position: relative; background-color: #e9ecef; display: flex; align-items: flex-start; justify-content: flex-start; overflow: auto; }
        
        .desktop-frame { transform-origin: top left; }

        .desktop-viewport, .mobile-viewport { flex-shrink: 0; background-color: #fff; will-change: transform, zoom; backface-visibility: hidden; }
        .desktop-frame { width: 1320px; height: 760px; padding: 20px; padding-top: 40px; border-radius: 10px; background: #f0f0f0; box-shadow: 0 8px 30px rgba(0,0,0,0.15); position: relative; }
        .desktop-header { position: absolute; top: 0; left: 0; width: 100%; height: 40px; background: #e0e0e0; border-top-left-radius: 10px; border-top-right-radius: 10px; display: flex; align-items: center; padding: 0 15px; }
        .dot { height: 12px; width: 12px; border-radius: 50%; margin-right: 8px; }
        .desktop-viewport { width: 1280px; height: 720px; box-shadow: 0 0 15px rgba(0,0,0,0.1); }
        .mobile-viewport { width: 375px; height: 812px; border-radius: 40px; box-shadow: 0 0 20px rgba(0,0,0,0.2); border: 10px solid #111; overflow: hidden; zoom: 1; margin: auto; }
        .preview-iframe { width: 100%; height: 100%; border: none; }
        .mobile-viewport .preview-iframe { border-radius: 30px; }

        .segmented-control { display: flex; position: relative; background-color: hsla(228, 50%, 62%, 1.00); border-radius: 8px; padding: 3px; width: fit-content; }
        .segmented-control__option { flex: 1; padding: 6px 16px; font-size: 0.8rem; font-weight: 600; color: #495057; background-color: transparent; border: none; cursor: pointer; transition: color 0.3s ease-in-out; z-index: 1; white-space: nowrap; }
        .segmented-control__glider { position: absolute; top: 3px; bottom: 3px; width: 49%; background-color: #ffffff; border-radius: 6px; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1); transition: transform 0.25s ease-in-out; z-index: 0; }
        .segmented-control__option:disabled { color: #adb5bd; cursor: not-allowed; }
      `}</style>
      <Container className="py-4" fluid>
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-0 fw-bold">⚽ Football Widget Builder</h2>
                <p className="text-muted mb-0">
                  Create and customize your football widget with ease.
                </p>
              </div>
              <div>
                <Button
                  color="secondary"
                  outline
                  className="me-2"
                  onClick={() => history.replace("/dashboard/football-widget")}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back
                </Button>
                <Button color="primary" onClick={handleCreate} disabled={isCreateDisabled}>
                  Create Widget
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={5}>
            <Card className="shadow-sm border-0">
              <CardHeader className="bg-white border-0 py-0 px-3">
                <div className="d-flex border-bottom border-light w-100">
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "1",
                    })}
                    onClick={() => toggleTab("1")}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General
                  </button>
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "2",
                    })}
                    onClick={() => toggleTab("2")}
                  >
                    <FontAwesomeIcon icon={faCogs} className="me-2" />
                    Advanced
                  </button>
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "3", 
                    })}
                    onClick={() => toggleTab("3")}
                    disabled={!formDetails.template} 
                  >
                    <FontAwesomeIcon icon={faPaintBrush} className="me-2" />
                    Template
                  </button>
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "4", 
                    })}
                    onClick={() => toggleTab("4")}
                  >
                    <FontAwesomeIcon icon={faAd} className="me-2" />
                    Ads
                  </button>              </div>
              </CardHeader>

              <CardBody className="p-4">
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <Form>
                      <h5 className="fw-bold mb-3">General Information</h5>
                      <div className="d-flex flex-wrap gap-3">
                        <FormGroup style={{ flex: "1 1 100%" }}>
                          <Label className="form-label">
                            Widget Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="widget_name"
                            className="truncate-placeholder"
                            value={formDetails.widget_name}
                            onChange={handleInputChange}
                            placeholder="E.g., Premier League Highlights"
                            autoComplete="off"
                          />
                        </FormGroup>
                        <FormGroup style={{ flex: "1 1 45%" }}>
                          <Label className="form-label">
                            Publisher <span className="text-danger">*</span>
                          </Label>
                          <CreatableSelect
                            styles={selectStyles}
                            isClearable
                            options={publisherOptions}
                            value={selectedPublisher}
                            onChange={handlePublisherChange}
                            onCreateOption={handleCreatePublisher}
                            placeholder="Select or Create Publisher"
                          />
                        </FormGroup>
                        <FormGroup style={{ flex: "1 1 45%" }}>
                          <Label className="form-label">
                            Template <span className="text-danger">*</span>
                          </Label>
                          <Select
                            styles={selectStyles}
                            options={templates}
                            value={templates.find(
                              (t) => t.value === formDetails.template
                            )}
                            onChange={(s) => {
                              const newTemplate = s ? s.value : "";
                              setFormDetails((p) => ({
                                ...p,
                                template: newTemplate,
                                layout: newTemplate === 'tournament' ? 'carousel' : '',
                                tournaments: [], 
                                tournament_slug: "",
                                rounds: [],
                                round_slug: "",
                                matches: [],
                                match_slugs: [], 
                                title: p.title, 
                                custom_css: p.custom_css, 
                                domains: p.domains, 
                                widget_data: {
                                  ...p.widget_data,
                                  template_data: {} 
                                },
                              }));
                              setSelectedTournament(null);
                              setSelectedRound(null);
                              setAllMatches([]);
                              setSelectedMatch(null);
                              setMatchSelectionType("all"); 

                              setSelectedMatchStatus(matchStatusOptions.find(opt => opt.value === 'all'));
                            }}
                          />
                        </FormGroup>
                        {formDetails.template === "tournament" && (
                          <FormGroup style={{ flex: "1 1 45%" }}>
                            <Label className="form-label">Layout</Label>
                            <Select
                              styles={selectStyles}
                              options={layouts}
                              value={layouts.find(
                                (l) => l.value === formDetails.layout
                              )}
                              onChange={handleLayoutChange}
                              placeholder="Select a layout"
                            />
                          </FormGroup>
                        )}
                        <FormGroup style={{ flex: "1 1 45%" }}>
                          <Label className="form-label">Title</Label>
                          <Input
                            name="title"
                            className="text truncate-placeholder"
                            value={formDetails.title}
                            onChange={handleInputChange}
                            placeholder="Widget Title (Optional)"
                          />
                        </FormGroup>
                      </div>

                      {showDataSource && (
                        <>
                          <hr className="mt-4 mb-3" />
                          <h5 className="fw-bold mb-3">Football Info</h5>
                          <div className="d-flex flex-wrap gap-3">
                            <FormGroup style={{ flex: "1 1 100%" }}>
                              <Label className="form-label">
                                Tournament{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Select
                                styles={selectStyles}
                                options={tournaments.map((t) => ({
                                  value: t.t_slug,
                                  label: t.name,
                                }))}
                                onChange={setSelectedTournament}
                                value={selectedTournament}
                              />
                            </FormGroup>
                            {formDetails.template !==
                              "tournament-points-table" && (
                                <FormGroup style={{ flex: "1 1 45%" }}>
                                  <Label className="form-label">Round</Label>
                                  <Select
                                    styles={selectStyles}
                                    options={tournamentRounds.map((r) => ({
                                      value: r.r_slug,
                                      label: r.name,
                                    }))}
                                    onChange={setSelectedRound}
                                    value={selectedRound}
                                    isDisabled={!selectedTournament}
                                    isClearable
                                  />
                                </FormGroup>
                              )}
                            {formDetails.template === "tournament" &&
                              selectedRound && (
                                <FormGroup style={{ width: "100%" }}>
                                  <Label className="form-label d-block">
                                    Match Selection
                                  </Label>
                                  <div className="segmented-control">
                                    <div
                                      className="segmented-control__glider"
                                      style={{ transform: `translateX(${matchSelectionType === "all" ? "0%" : "100%"})` }}
                                    />
                                    <button
                                      type="button"
                                      className="segmented-control__option"
                                      onClick={() => {
                                        setMatchSelectionType("all");
                                        setSelectedMatch(null);
                                      }}
                                    >
                                      All Matches
                                    </button>
                                    <button
                                      type="button"
                                      className="segmented-control__option"
                                      onClick={() => setMatchSelectionType("single")}
                                      disabled={noMatchesFound}
                                    >
                                      Single Match
                                    </button>
                                  </div>
                                </FormGroup>
                              )}

                            {(formDetails.template === "full-match-detail" ||
                              formDetails.template === "tournament") && (
                                <FormGroup style={{ flex: "1 1 45%" }}>
                                  <Label className="form-label">
                                    Filter Match List by Status
                                  </Label>
                                  <Select
                                    styles={selectStyles}
                                    options={matchStatusOptions}
                                    onChange={setSelectedMatchStatus}
                                    value={selectedMatchStatus}
                                    isDisabled={!selectedRound || noMatchesFound}
                                    isClearable
                                  />
                                </FormGroup>
                              )}

                            {(formDetails.template === "full-match-detail" ||
                              (formDetails.template === "tournament" &&
                                matchSelectionType === "single")) && (
                                <FormGroup style={{ flex: "1 1 45%" }}>
                                  <Label className="form-label">Match</Label>
                                  <Select
                                    styles={selectStyles}
                                    options={matches.map((m) => ({
                                      value: m.match.m_slug,
                                      label: m.match.title,
                                    }))}
                                    onChange={setSelectedMatch}
                                    value={selectedMatch}
                                    isDisabled={!selectedRound || noMatchesFound || noMatchesForFilter}
                                    placeholder={
                                      noMatchesFound
                                        ? "No matches available"
                                        : noMatchesForFilter
                                          ? `No ${selectedMatchStatus?.label || ''} matches`
                                          : "Select one match"
                                    }
                                  />
                                </FormGroup>
                              )}
                          </div>
                        </>
                      )}
                    </Form>
                  </TabPane>
                  <TabPane tabId="2">
                    <h5 className="fw-bold mb-3">Advanced Configuration</h5>
                    <div className="d-flex flex-wrap gap-3">
                      <div style={{ flex: "1 1 100%" }}>
                        {widgetConfig.additionalSettings ? (
                          <AdditionalSettings
                            additionalSettings={
                              widgetConfig.additionalSettings
                            }
                            formDetails={formDetails}
                            setFormDetails={setFormDetails}
                          />
                        ) : (
                          <p className="text-center text-muted">
                            No additional settings available.
                          </p>
                        )}
                      </div>
                      <div style={{ flex: "1 1 100%" }}>
                        <FormGroup>
                          <Label className="form-label">Custom CSS</Label>
                          <Input
                            type="textarea"
                            name="custom_css"
                            value={formDetails.custom_css}
                            onChange={handleInputChange}
                            placeholder="Enter any custom CSS rules..."
                            rows={2}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">Allowed Domains</Label>
                          {formDetails.domains.map((item, index) => (
                            <div key={index} className="d-flex mb-2">
                              <Input
                                type="text"
                                bsSize="sm"
                                placeholder="e.g., example.com or *.example.com"
                                value={item.domain}
                                onChange={(e) =>
                                  handleDomainChange(index, e.target.value)
                                }
                              />
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                className="ms-2"
                                onClick={() => handleRemoveDomain(index)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                              {index === formDetails.domains.length - 1 && (
                                <Button
                                  color="primary"
                                  outline
                                  size="sm"
                                  className="ms-2"
                                  onClick={handleAddDomain}
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                </Button>
                              )}
                            </div>
                          ))}
                          <FormText>
                            Use `*.example.com` for wildcards. Leave empty to
                            allow all domains.
                          </FormText>
                        </FormGroup>
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId="3">
                    <TemplateSettings
                      template={formDetails.template}
                      templateData={formDetails.widget_data.template_data}
                      setFormDetails={setFormDetails}
                      formDetails={formDetails}
                    />
                  </TabPane>
                  <TabPane tabId="4">
                    <h5 className="fw-bold mb-3">Advertisement Settings</h5>
                    {widgetConfig.adSettings ? (
                      <AdSettings
                        adConfig={widgetConfig.adSettings}
                        formDetails={formDetails}
                        setFormDetails={setFormDetails}
                      />
                    ) : (
                      <p className="text-center text-muted">
                        No ad settings available.
                      </p>
                    )}
                  </TabPane>

                </TabContent>
              </CardBody>
            </Card>
          </Col>

          <Col md={7}>
            <div className="preview-wrapper">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold mb-0">Live Preview</h4>
                <div className="d-flex align-items-center">
                  <ButtonGroup size="sm" className="me-3">
                    <Button
                      outline
                      color="primary"
                      active={previewMode === "desktop"}
                      onClick={() => handleModeChange("desktop")}
                    >
                      <FontAwesomeIcon icon={faDesktop} />
                    </Button>
                    <Button
                      outline
                      color="primary"
                      active={previewMode === "mobile"}
                      onClick={() => handleModeChange("mobile")}
                    >
                      <FontAwesomeIcon icon={faMobileAlt} />
                    </Button>
                  </ButtonGroup>
                  {previewMode === "desktop" && (
                    <>
                      <ButtonGroup size="sm">
                        <Button
                          outline
                          color="secondary"
                          onClick={() =>
                            setPreviewScale((s) => Math.max(0.1, s - 0.1))
                          }
                        >
                          <FontAwesomeIcon icon={faSearchMinus} />
                        </Button>
                        <Button
                          outline
                          color="secondary"
                          onClick={fitPreviewToScreen}
                        >
                          <FontAwesomeIcon icon={faExpandArrowsAlt} />
                        </Button>
                        <Button
                          outline
                          color="secondary"
                          onClick={() => setPreviewScale((s) => s + 0.1)}
                        >
                          <FontAwesomeIcon icon={faSearchPlus} />
                        </Button>
                      </ButtonGroup>
                      <small
                        className="text-muted ms-2 fw-bold"
                        style={{ minWidth: "40px", textAlign: "right" }}
                      >
                        {Math.round(previewScale * 100)}%
                      </small>
                    </>
                  )}
                </div>
              </div>
              <div
                className="preview-iframe-container"
                ref={previewContainerRef}
              >
                {isFetchingPreview && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255,255,255,0.7)",
                      zIndex: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Spinner color="primary">Loading...</Spinner>
                  </div>
                )}
                {previewMode === "desktop" ? (
                  <div style={{ width: 1320 * previewScale, height: 760 * previewScale, flexShrink: 0, margin: 'auto' }}>
                    <div
                      className="desktop-frame"
                      style={{ transform: `scale(${previewScale})` }}
                    >
                      <div className="desktop-header">
                        <div
                          className="dot"
                          style={{ background: "#ff5f56" }}
                        ></div>
                        <div
                          className="dot"
                          style={{ background: "#ffbd2e" }}
                        ></div>
                        <div
                          className="dot"
                          style={{ background: "#27c93f" }}
                        ></div>
                      </div>
                      <div className="desktop-viewport">
                        <iframe
                          srcDoc={previewHtml}
                          title="Widget Preview"
                          className="preview-iframe"
                          id="preview-iframe"
                          sandbox="allow-scripts"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mobile-viewport">
                    <iframe
                      srcDoc={previewHtml}
                      title="Widget Preview"
                      className="preview-iframe"
                      id="preview-iframe"
                      sandbox="allow-scripts"
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default CreateFootballWidget;